// Pure input math — no DOM, fully unit-tested.

export interface Vec2 {
  x: number
  y: number
}

/**
 * Scaled radial dead zone (the correct one for 360° movement — axial clamping
 * snaps diagonals to cardinals). Magnitude remaps [inner..outer] → [0..1]
 * smoothly, so there's no discontinuity at the dead-zone edge and worn sticks
 * that never reach 1.0 diagonally still hit full speed.
 */
export function scaledRadialDeadzone(v: Vec2, inner: number, outer: number): Vec2 {
  const len = Math.hypot(v.x, v.y)
  if (len <= inner) return { x: 0, y: 0 }
  const scaled = Math.min(1, (len - inner) / (outer - inner))
  return { x: (v.x / len) * scaled, y: (v.y / len) * scaled }
}

/**
 * Digital keys → normalized vector (Godot get_vector-style composite):
 * diagonals aren't 41% faster, opposing keys cancel. World +y is up on screen
 * (camera.screenToWorld flips), so "up" → +y.
 */
export function compositeKeyVector(up: boolean, down: boolean, left: boolean, right: boolean): Vec2 {
  let x = 0
  let y = 0
  if (up) y += 1
  if (down) y -= 1
  if (left) x -= 1
  if (right) x += 1
  const len = Math.hypot(x, y)
  return len > 0 ? { x: x / len, y: y / len } : { x: 0, y: 0 }
}
